import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bookmark, Tag, User, ShoppingBag, FileText, Briefcase, MapPin, DollarSign, Building2 } from "lucide-react";
import { getBookmarks, deleteBookmark, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function SavedPostCard({ post, onRemove }) {
  const navigate = useNavigate();

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <Card className="bg-white border-[#E5E0D5] shadow-sm hover:shadow-md transition-all duration-300" data-testid={`saved-post-${post.post_id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate(`/profile/${post.author?.user_id}`)}
          >
            <Avatar className="h-9 w-9 border border-[#E5E0D5]">
              <AvatarImage src={post.author?.picture} />
              <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs font-['Nunito']">
                {post.author?.name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <span className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] hover:underline">
                {post.author?.name}
              </span>
              <p className="text-xs text-[#6B665E] font-['Nunito']">{timeAgo(post.created_at)}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(post.bookmark_id)}
            className="h-8 w-8 p-0 text-[var(--liturgical-secondary)] hover:text-[var(--liturgical-primary)] hover:bg-red-50"
            data-testid={`remove-saved-post-${post.post_id}`}
          >
            <Bookmark className="w-4 h-4 fill-current" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed line-clamp-3">{post.content}</p>
        {post.image_url && (
          <img
            src={resolveImageUrl(post.image_url)}
            alt=""
            className="w-full rounded-md border border-[#E5E0D5] max-h-48 object-cover"
          />
        )}
      </CardContent>
    </Card>
  );
}

function SavedListingCard({ listing, onRemove }) {
  const navigate = useNavigate();

  return (
    <Card
      className="bg-white border-[#E5E0D5] shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
      data-testid={`saved-listing-${listing.listing_id}`}
    >
      {listing.image_url && (
        <div className="overflow-hidden">
          <img
            src={resolveImageUrl(listing.image_url)}
            alt={listing.title}
            className="w-full h-40 object-cover"
          />
        </div>
      )}
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26] leading-tight line-clamp-1">
            {listing.title}
          </h3>
          <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-base whitespace-nowrap">
            ${listing.price}
          </span>
        </div>
        <p className="text-xs text-[#6B665E] font-['Nunito'] line-clamp-2">{listing.description}</p>
        <div className="flex items-center justify-between pt-2">
          <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[10px] font-['Nunito']">
            <Tag className="w-2.5 h-2.5 mr-1" />
            {listing.category}
          </Badge>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/profile/${listing.seller?.user_id}`)}
              className="h-7 px-2 text-[#6B665E] hover:text-[#2D2A26] text-xs font-['Nunito']"
            >
              <User className="w-3 h-3 mr-1" />
              {listing.seller?.name}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(listing.bookmark_id)}
              className="h-7 w-7 p-0 text-[var(--liturgical-secondary)] hover:text-[var(--liturgical-primary)] hover:bg-red-50"
              data-testid={`remove-saved-listing-${listing.listing_id}`}
            >
              <Bookmark className="w-3.5 h-3.5 fill-current" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SavedPage({ user }) {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState({ posts: [], listings: [], jobs: [] });
  const [activeTab, setActiveTab] = useState("posts");
  const [loading, setLoading] = useState(true);

  const loadBookmarks = async () => {
    setLoading(true);
    try {
      const res = await getBookmarks();
      setBookmarks({ posts: res.data.posts || [], listings: res.data.listings || [], jobs: res.data.jobs || [] });
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    loadBookmarks();
  }, []);

  const handleRemove = async (bookmarkId) => {
    try {
      await deleteBookmark(bookmarkId);
      setBookmarks({
        posts: bookmarks.posts.filter((p) => p.bookmark_id !== bookmarkId),
        listings: bookmarks.listings.filter((l) => l.bookmark_id !== bookmarkId),
        jobs: bookmarks.jobs.filter((j) => j.bookmark_id !== bookmarkId),
      });
      toast.success("Removed from saved");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const totalSaved = bookmarks.posts.length + bookmarks.listings.length + bookmarks.jobs.length;

  const formatSalary = (min, max, period) => {
    if (!min && !max) return null;
    const fmt = (n) => n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`;
    const p = period === "hourly" ? "/hr" : period === "monthly" ? "/mo" : "/yr";
    if (min && max) return `${fmt(min)} - ${fmt(max)}${p}`;
    if (min) return `From ${fmt(min)}${p}`;
    return `Up to ${fmt(max)}${p}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="saved-page">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight" data-testid="saved-title">Saved Items</h1>
          <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">{totalSaved} {totalSaved === 1 ? "item" : "items"} saved</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="saved-tabs">
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5]">
            <TabsTrigger value="posts" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="saved-tab-posts">
              <FileText className="w-4 h-4 mr-2" />Posts ({bookmarks.posts.length})
            </TabsTrigger>
            <TabsTrigger value="listings" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="saved-tab-listings">
              <ShoppingBag className="w-4 h-4 mr-2" />Listings ({bookmarks.listings.length})
            </TabsTrigger>
            <TabsTrigger value="jobs" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="saved-tab-jobs">
              <Briefcase className="w-4 h-4 mr-2" />Jobs ({bookmarks.jobs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="mt-6">
            {loading ? (
              <div className="text-center py-16 text-[#6B665E] font-['Nunito']">Loading...</div>
            ) : bookmarks.posts.length === 0 ? (
              <div className="text-center py-16" data-testid="empty-saved-posts">
                <FileText className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">No saved posts yet</p>
                <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Bookmark posts to find them here</p>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {bookmarks.posts.map((post) => (
                  <SavedPostCard key={post.post_id} post={post} onRemove={handleRemove} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="listings" className="mt-6">
            {loading ? (
              <div className="text-center py-16 text-[#6B665E] font-['Nunito']">Loading...</div>
            ) : bookmarks.listings.length === 0 ? (
              <div className="text-center py-16" data-testid="empty-saved-listings">
                <ShoppingBag className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">No saved listings yet</p>
                <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Bookmark marketplace items to find them here</p>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {bookmarks.listings.map((listing) => (
                  <SavedListingCard key={listing.listing_id} listing={listing} onRemove={handleRemove} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            {loading ? (
              <div className="text-center py-16 text-[#6B665E] font-['Nunito']">Loading...</div>
            ) : bookmarks.jobs.length === 0 ? (
              <div className="text-center py-16" data-testid="empty-saved-jobs">
                <Briefcase className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">No saved jobs yet</p>
                <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Bookmark jobs on the Job Board to find them here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bookmarks.jobs.map((job) => {
                  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_period);
                  return (
                    <Card key={job.job_id} className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate("/jobs")} data-testid={`saved-job-${job.job_id}`}>
                      <CardContent className="p-4">
                        <div className="flex gap-3 items-start">
                          {job.business_photo ? (
                            <img src={job.business_photo} alt="" className="w-10 h-10 rounded-lg object-cover border border-[#E5E0D5] flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-[var(--liturgical-primary)]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">{job.title}</h3>
                                <p className="text-xs text-[#6B665E] font-['Nunito']">{job.business_name}</p>
                              </div>
                              <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleRemove(job.bookmark_id); }}
                                className="h-8 w-8 p-0 text-[var(--liturgical-primary)]" data-testid={`remove-saved-job-${job.job_id}`}>
                                <Bookmark className="w-4 h-4 fill-current" />
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1.5">
                              <Badge variant="outline" className="text-[10px] font-['Nunito'] capitalize">{job.job_type}</Badge>
                              <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1"><MapPin className="w-3 h-3" />{job.location}</span>
                              {salary && <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1"><DollarSign className="w-3 h-3" />{salary}</span>}
                              {job.status !== "active" && <Badge variant="outline" className="text-[10px] border-red-200 text-red-600">{job.status}</Badge>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
